import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../api/client";

// ─── Label / colour maps ──────────────────────────────────────────────────────

const PRESET_LABELS = {
  BACK_OFFICE: "Back Office",
  MANAGEMENT:  "Management",
  ADMIN:       "Admin",
  CUSTOM:      "Custom",
};

// Maps preset → underlying backend role (used by middleware requireRole)
const PRESET_ROLE_MAP = {
  BACK_OFFICE: "EDITOR",
  MANAGEMENT:  "VIEWER",
  ADMIN:       "ADMIN",
};

const STATUS_LABEL = {
  ACTIVE:   "פעיל",
  INACTIVE: "לא פעיל",
  PENDING:  "ממתין לאישור",
  REJECTED: "נדחה",
};
const STATUS_COLOR = {
  ACTIVE:   "var(--success)",
  INACTIVE: "var(--danger)",
  PENDING:  "var(--warning, #f59e0b)",
  REJECTED: "var(--danger)",
};

const ROLE_LABEL = { ADMIN: "מנהל", EDITOR: "עורך", VIEWER: "צופה" };

const ACTION_LABEL = {
  CREATED: "נוצר", UPDATED: "עודכן", DELETED: "נמחק",
  APPROVED: "אושר", REJECTED: "נדחה", ENDED: "הושלם",
  DRAW: "משיכה", REPAY: "החזר", CREATE: "נוצר", UPDATE: "עודכן", DELETE: "נמחק",
};
const ACTION_COLOR = {
  CREATED: "var(--success)", CREATE: "var(--success)",
  UPDATED: "var(--accent)",  UPDATE: "var(--accent)",
  DELETED: "var(--danger)",  DELETE: "var(--danger)",
  APPROVED: "var(--success)", REJECTED: "var(--danger)",
  ENDED: "var(--text-secondary)", DRAW: "var(--warning, #f59e0b)", REPAY: "var(--success)",
};
const ENTITY_LABEL = {
  LOAN: "הלוואה", USER: "משתמש", CreditFacility: "מסגרת אשראי",
  Collateral: "בטחון", CashFlowEntry: "תזרים",
};

function formatDate(d, short = false) {
  if (!d) return "—";
  const x = new Date(d);
  if (short) return x.toLocaleDateString("he-IL");
  return x.toLocaleDateString("he-IL") + " " + x.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
}

function Badge({ label, color }) {
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 99,
      fontSize: 12, fontWeight: 600,
      background: color + "22", color,
    }}>
      {label}
    </span>
  );
}

// ─── Scope badge ──────────────────────────────────────────────────────────────
const SCOPE_STYLES = {
  VIEW:   { bg: "var(--accent)1a",        color: "var(--accent)"  },
  WRITE:  { bg: "rgba(245,158,11,0.1)",   color: "#f59e0b"        },
  ACCESS: { bg: "var(--success)1a",       color: "var(--success)" },
  ADMIN:  { bg: "var(--danger)1a",        color: "var(--danger)"  },
};
function ScopeBadge({ scope }) {
  const s = SCOPE_STYLES[scope] || SCOPE_STYLES.VIEW;
  return (
    <span style={{
      display: "inline-block", padding: "1px 7px", borderRadius: 99,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.03em",
      background: s.bg, color: s.color, flexShrink: 0,
    }}>
      {scope}
    </span>
  );
}

// ─── Toggle switch ────────────────────────────────────────────────────────────
function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        position: "relative", width: 36, height: 20, borderRadius: 10,
        border: "none", cursor: "pointer",
        background: checked ? "var(--accent)" : "var(--border-color, #3a3a4a)",
        transition: "background 0.2s", flexShrink: 0, padding: 0,
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: checked ? 18 : 2,
        width: 16, height: 16, borderRadius: "50%", background: "#fff",
        transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
      }} />
    </button>
  );
}

// ─── Permission sections schema ───────────────────────────────────────────────
const PERM_SECTIONS = [
  {
    key: "routes", label: "Routes / Access",
    items: [
      { key: "dashboard",        label: "Dashboard",         scope: "ACCESS" },
      { key: "loans",            label: "Loans",             scope: "ACCESS" },
      { key: "loanDetail",       label: "Loan Detail",       scope: "ACCESS" },
      { key: "collateral",       label: "Collateral",        scope: "ACCESS" },
      { key: "creditFacilities", label: "Credit Facilities", scope: "ACCESS" },
      { key: "balanceSheet",     label: "Balance Sheet",     scope: "ACCESS" },
      { key: "incomeStatement",  label: "Income Statement",  scope: "ACCESS" },
      { key: "cashFlow",         label: "Cash Flow",         scope: "ACCESS" },
      { key: "financials",       label: "Financials",        scope: "ACCESS" },
      { key: "reports",          label: "Reports",           scope: "ACCESS" },
      { key: "userManagement",   label: "User Management",   scope: "ADMIN"  },
      { key: "activityLog",      label: "Activity Log",      scope: "ADMIN"  },
    ],
  },
  {
    key: "dashboard", label: "Dashboard",
    items: [
      { key: "alerts",         label: "Alerts",            scope: "VIEW" },
      { key: "kpiTotalDebt",   label: "KPI — Total Debt",  scope: "VIEW" },
      { key: "kpiAvgRate",     label: "KPI — Avg Rate",    scope: "VIEW" },
      { key: "kpiAvgLtv",      label: "KPI — Avg LTV",     scope: "VIEW" },
      { key: "kpiUtilization", label: "KPI — Utilization", scope: "VIEW" },
      { key: "upcomingEvents", label: "Upcoming Events",   scope: "VIEW" },
      { key: "stockPortfolio", label: "Stock Portfolio",   scope: "VIEW" },
      { key: "lenderChart",    label: "Lender Chart",      scope: "VIEW" },
    ],
  },
  {
    key: "loans", label: "Loans",
    items: [
      { key: "viewTable",   label: "View Table",   scope: "VIEW"  },
      { key: "viewMetrics", label: "View Metrics", scope: "VIEW"  },
      { key: "viewRisk",    label: "View Risk",    scope: "VIEW"  },
      { key: "exportCsv",   label: "Export CSV",   scope: "VIEW"  },
      { key: "exportPdf",   label: "Export PDF",   scope: "VIEW"  },
      { key: "create",      label: "Create",       scope: "WRITE" },
      { key: "edit",        label: "Edit",         scope: "WRITE" },
      { key: "close",       label: "Close",        scope: "WRITE" },
      { key: "hardDelete",  label: "Hard Delete",  scope: "ADMIN" },
    ],
  },
  {
    key: "loanDetail", label: "Loan Detail",
    items: [
      { key: "viewKpis",       label: "View KPIs",          scope: "VIEW"  },
      { key: "viewGeneral",    label: "View General",       scope: "VIEW"  },
      { key: "viewFinancial",  label: "View Financial",     scope: "VIEW"  },
      { key: "viewCollateral", label: "View Collateral",    scope: "VIEW"  },
      { key: "viewAmortTable", label: "Amortization Table", scope: "VIEW"  },
      { key: "viewAmortChart", label: "Amortization Chart", scope: "VIEW"  },
      { key: "viewNotes",      label: "View Notes",         scope: "VIEW"  },
      { key: "viewHistory",    label: "View History",       scope: "VIEW"  },
      { key: "edit",           label: "Edit",               scope: "WRITE" },
      { key: "breachNote",     label: "Breach Note",        scope: "WRITE" },
    ],
  },
  {
    key: "collateral", label: "Collateral",
    items: [
      { key: "viewPulse",  label: "View Pulse",  scope: "VIEW"  },
      { key: "viewGauges", label: "View Gauges", scope: "VIEW"  },
      { key: "viewCards",  label: "View Cards",  scope: "VIEW"  },
      { key: "create",     label: "Create",      scope: "WRITE" },
      { key: "edit",       label: "Edit",        scope: "WRITE" },
      { key: "remove",     label: "Delete",      scope: "WRITE" },
    ],
  },
  {
    key: "creditFacilities", label: "Credit Facilities",
    items: [
      { key: "viewKpis",  label: "View KPIs",  scope: "VIEW"  },
      { key: "viewCards", label: "View Cards", scope: "VIEW"  },
      { key: "draw",      label: "Draw",       scope: "WRITE" },
      { key: "repay",     label: "Repay",      scope: "WRITE" },
    ],
  },
  {
    key: "balanceSheet", label: "Balance Sheet",
    items: [
      { key: "viewAssets",        label: "View Assets",        scope: "VIEW"  },
      { key: "viewPie",           label: "View Pie Chart",     scope: "VIEW"  },
      { key: "viewLiabilities",   label: "View Liabilities",   scope: "VIEW"  },
      { key: "viewEquity",        label: "View Equity",        scope: "VIEW"  },
      { key: "manageAssets",      label: "Manage Assets",      scope: "WRITE" },
      { key: "manageLiabilities", label: "Manage Liabilities", scope: "WRITE" },
    ],
  },
  {
    key: "incomeStatement", label: "Income Statement",
    items: [
      { key: "viewKpis",    label: "View KPIs",    scope: "VIEW" },
      { key: "viewRevenue", label: "View Revenue", scope: "VIEW" },
      { key: "viewExpense", label: "View Expense", scope: "VIEW" },
      { key: "viewChart",   label: "View Chart",   scope: "VIEW" },
    ],
  },
  {
    key: "cashFlow", label: "Cash Flow",
    items: [
      { key: "viewSegments", label: "View Segments", scope: "VIEW"  },
      { key: "viewEntries",  label: "View Entries",  scope: "VIEW"  },
      { key: "viewChart",    label: "View Chart",    scope: "VIEW"  },
      { key: "viewTable",    label: "View Table",    scope: "VIEW"  },
      { key: "add",          label: "Add",           scope: "WRITE" },
      { key: "edit",         label: "Edit",          scope: "WRITE" },
      { key: "remove",       label: "Delete",        scope: "WRITE" },
    ],
  },
  {
    key: "reports", label: "Reports",
    items: [
      { key: "viewDaily",   label: "View Daily Reports", scope: "VIEW" },
      { key: "downloadPdf", label: "Download PDF",       scope: "VIEW" },
    ],
  },
  {
    key: "userManagement", label: "User Management",
    items: [
      { key: "viewPending",     label: "View Pending",      scope: "ADMIN" },
      { key: "viewUsers",       label: "View Users",        scope: "ADMIN" },
      { key: "viewApprovalLog", label: "View Approval Log", scope: "ADMIN" },
      { key: "viewFeedback",    label: "View Feedback",     scope: "ADMIN" },
      { key: "approve",         label: "Approve / Reject",  scope: "ADMIN" },
      { key: "editPermissions", label: "Edit Permissions",  scope: "ADMIN" },
      { key: "deleteUser",      label: "Delete User",       scope: "ADMIN" },
    ],
  },
  {
    key: "activityLog", label: "Activity Log",
    items: [
      { key: "view", label: "View Activity Log", scope: "ADMIN" },
    ],
  },
];

// ─── Preset permission values (mirrors backend/config/permissionPresets.js) ───
const PRESETS = {
  BACK_OFFICE: {
    routes: { dashboard: true, loans: true, loanDetail: true, collateral: true, creditFacilities: true, balanceSheet: true, incomeStatement: false, cashFlow: true, financials: false, reports: true, userManagement: false, activityLog: false },
    dashboard: { alerts: true, kpiTotalDebt: false, kpiAvgRate: false, kpiAvgLtv: false, kpiUtilization: false, upcomingEvents: true, stockPortfolio: true, lenderChart: false },
    loans: { viewTable: true, viewMetrics: false, viewRisk: true, exportCsv: true, exportPdf: true, create: true, edit: true, close: true, hardDelete: false },
    loanDetail: { viewKpis: true, viewGeneral: true, viewFinancial: true, viewCollateral: true, viewAmortTable: true, viewAmortChart: true, viewNotes: true, viewHistory: true, edit: true, breachNote: true },
    collateral: { viewPulse: true, viewGauges: true, viewCards: true, create: true, edit: true, remove: true },
    creditFacilities: { viewKpis: false, viewCards: true, draw: true, repay: true },
    balanceSheet: { viewAssets: false, viewPie: false, viewLiabilities: false, viewEquity: false, manageAssets: true, manageLiabilities: true },
    incomeStatement: { viewKpis: false, viewRevenue: false, viewExpense: false, viewChart: false },
    cashFlow: { viewSegments: true, viewEntries: true, viewChart: true, viewTable: true, add: true, edit: true, remove: true },
    reports: { viewDaily: true, downloadPdf: true },
    userManagement: { viewPending: false, viewUsers: false, viewApprovalLog: false, viewFeedback: false, approve: false, editPermissions: false, deleteUser: false },
    activityLog: { view: false },
  },
  MANAGEMENT: {
    routes: { dashboard: true, loans: true, loanDetail: true, collateral: true, creditFacilities: true, balanceSheet: true, incomeStatement: true, cashFlow: true, financials: true, reports: true, userManagement: false, activityLog: false },
    dashboard: { alerts: true, kpiTotalDebt: true, kpiAvgRate: true, kpiAvgLtv: true, kpiUtilization: true, upcomingEvents: true, stockPortfolio: true, lenderChart: true },
    loans: { viewTable: true, viewMetrics: true, viewRisk: true, exportCsv: true, exportPdf: true, create: false, edit: false, close: false, hardDelete: false },
    loanDetail: { viewKpis: true, viewGeneral: true, viewFinancial: true, viewCollateral: true, viewAmortTable: true, viewAmortChart: true, viewNotes: true, viewHistory: true, edit: false, breachNote: true },
    collateral: { viewPulse: true, viewGauges: true, viewCards: true, create: false, edit: false, remove: false },
    creditFacilities: { viewKpis: true, viewCards: true, draw: false, repay: false },
    balanceSheet: { viewAssets: true, viewPie: true, viewLiabilities: true, viewEquity: true, manageAssets: true, manageLiabilities: true },
    incomeStatement: { viewKpis: true, viewRevenue: true, viewExpense: true, viewChart: true },
    cashFlow: { viewSegments: true, viewEntries: true, viewChart: true, viewTable: true, add: true, edit: true, remove: true },
    reports: { viewDaily: true, downloadPdf: true },
    userManagement: { viewPending: false, viewUsers: false, viewApprovalLog: false, viewFeedback: false, approve: false, editPermissions: false, deleteUser: false },
    activityLog: { view: false },
  },
  ADMIN: {
    routes: { dashboard: true, loans: true, loanDetail: true, collateral: true, creditFacilities: true, balanceSheet: true, incomeStatement: true, cashFlow: true, financials: true, reports: true, userManagement: true, activityLog: true },
    dashboard: { alerts: true, kpiTotalDebt: true, kpiAvgRate: true, kpiAvgLtv: true, kpiUtilization: true, upcomingEvents: true, stockPortfolio: true, lenderChart: true },
    loans: { viewTable: true, viewMetrics: true, viewRisk: true, exportCsv: true, exportPdf: true, create: true, edit: true, close: true, hardDelete: true },
    loanDetail: { viewKpis: true, viewGeneral: true, viewFinancial: true, viewCollateral: true, viewAmortTable: true, viewAmortChart: true, viewNotes: true, viewHistory: true, edit: true, breachNote: true },
    collateral: { viewPulse: true, viewGauges: true, viewCards: true, create: true, edit: true, remove: true },
    creditFacilities: { viewKpis: true, viewCards: true, draw: true, repay: true },
    balanceSheet: { viewAssets: true, viewPie: true, viewLiabilities: true, viewEquity: true, manageAssets: true, manageLiabilities: true },
    incomeStatement: { viewKpis: true, viewRevenue: true, viewExpense: true, viewChart: true },
    cashFlow: { viewSegments: true, viewEntries: true, viewChart: true, viewTable: true, add: true, edit: true, remove: true },
    reports: { viewDaily: true, downloadPdf: true },
    userManagement: { viewPending: true, viewUsers: true, viewApprovalLog: true, viewFeedback: true, approve: true, editPermissions: true, deleteUser: true },
    activityLog: { view: true },
  },
};

function deepEqual(a, b) { return JSON.stringify(a) === JSON.stringify(b); }

// ─── Collapsible permission section ──────────────────────────────────────────
function PermSection({ section, perms, onChange }) {
  const [open, setOpen] = useState(false);
  const sectionPerms = perms[section.key] || {};
  return (
    <div style={{ border: "1px solid var(--border-light)", borderRadius: "var(--radius)", overflow: "hidden", marginBottom: 5 }}>
      <button
        type="button" onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "9px 14px", background: "var(--bg-subtle)", border: "none", cursor: "pointer",
          color: "var(--text-primary, var(--text))", fontSize: 13, fontWeight: 600, textAlign: "right",
        }}
      >
        <span>{section.label}</span>
        <span style={{ fontSize: 10, opacity: 0.5, display: "inline-block", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.22s" }}>▾</span>
      </button>
      {open && (
        <div>
          {section.items.map((item, idx) => {
            const val = Boolean(sectionPerms[item.key]);
            return (
              <div key={item.key} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "7px 14px",
                borderTop: "1px solid var(--border-light)",
                background: val ? "var(--success)08" : "transparent",
              }}>
                <ToggleSwitch checked={val} onChange={(next) => onChange(section.key, item.key, next)} />
                <span style={{ flex: 1, fontSize: 13, color: "var(--text-primary, var(--text))" }}>{item.label}</span>
                <ScopeBadge scope={item.scope} />
                <span style={{ fontSize: 11, fontWeight: 700, minWidth: 16, textAlign: "center", color: val ? "var(--success)" : "var(--text-secondary)" }}>
                  {val ? "Y" : "N"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Pill button helper ───────────────────────────────────────────────────────
function PillBtn({ label, active, onClick, color }) {
  const accentColor = color || "var(--accent)";
  return (
    <button
      type="button" onClick={onClick}
      style={{
        padding: "6px 16px", borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: "pointer",
        border: active ? `1.5px solid ${accentColor}` : "1.5px solid var(--border-light)",
        background: active ? accentColor : "transparent",
        color: active ? "#fff" : "var(--text-secondary)",
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

// ─── Main page export ─────────────────────────────────────────────────────────
export default function UserProfilePage({ currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [tab, setTab]         = useState("activity");
  const [editing, setEditing] = useState(false);

  // Edit state — all in one object so one save button handles everything
  const [editStatus, setEditStatus]       = useState("ACTIVE");
  const [activePreset, setActivePreset]   = useState("BACK_OFFICE");
  const [editRole, setEditRole]           = useState("EDITOR"); // system role for Custom mode
  const [perms, setPerms]                 = useState(() => JSON.parse(JSON.stringify(PRESETS.BACK_OFFICE)));
  const [saving, setSaving]               = useState(false);
  const [saveError, setSaveError]         = useState("");

  const fetchData = () => {
    setLoading(true);
    apiClient.get(`/auth/users/${id}`)
      .then((res) => {
        const u = res.data.user;
        setData(res.data);
        // seed edit state from fetched user
        setEditStatus(u.status === "ACTIVE" ? "ACTIVE" : "INACTIVE");
        const preset = u.rolePreset || "BACK_OFFICE";
        setActivePreset(preset);
        setEditRole(u.role || "EDITOR");
        const userPerms = u.permissions || {};
        setPerms(
          userPerms.routes
            ? JSON.parse(JSON.stringify(userPerms))
            : JSON.parse(JSON.stringify(PRESETS[preset] || PRESETS.BACK_OFFICE))
        );
      })
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-detect if perms match a standard preset
  useEffect(() => {
    if (activePreset === "CUSTOM") return;
    for (const [key, preset] of Object.entries(PRESETS)) {
      if (deepEqual(perms, preset)) { setActivePreset(key); return; }
    }
    setActivePreset("CUSTOM");
  }, [perms]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePresetClick = (pk) => {
    if (pk === "CUSTOM") { setActivePreset("CUSTOM"); return; }
    const preset = PRESETS[pk];
    if (!preset) return;
    setPerms(JSON.parse(JSON.stringify(preset)));
    setActivePreset(pk);
    setEditRole(PRESET_ROLE_MAP[pk] || "VIEWER");
  };

  const handleToggle = (sectionKey, itemKey, value) => {
    setPerms((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next[sectionKey]) next[sectionKey] = {};
      next[sectionKey][itemKey] = value;
      return next;
    });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setSaveError("");
    try {
      if (activePreset !== "CUSTOM") {
        // apply-preset sets role + permissions in one shot
        await apiClient.post(`/auth/users/${id}/apply-preset`, { preset: activePreset });
        // save status separately (apply-preset doesn't touch status)
        await apiClient.put(`/auth/users/${id}`, { status: editStatus === "ACTIVE" ? "ACTIVE" : "INACTIVE" });
      } else {
        await apiClient.put(`/auth/users/${id}`, {
          role: editRole,
          status: editStatus === "ACTIVE" ? "ACTIVE" : "INACTIVE",
          permissions: perms,
          rolePreset: "CUSTOM",
        });
      }
      await fetchData();
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page" style={{ padding: "var(--space-xl)" }}>טוען...</div>;
  if (error)   return <div className="page" style={{ padding: "var(--space-xl)", color: "var(--danger)" }}>{error}</div>;
  if (!data)   return null;

  const { user, activity, approvals } = data;
  const isSelf    = String(user._id) === String(currentUser?.id);
  const isPending = user.status === "PENDING";

  // Friendly role label shown in header
  const displayRole   = PRESET_LABELS[user.rolePreset] || ROLE_LABEL[user.role] || user.role;
  const displayStatus = STATUS_LABEL[user.status] || user.status;
  const statusColor   = STATUS_COLOR[user.status] || "var(--text-secondary)";

  return (
    <div className="page" style={{ width: "100%", boxSizing: "border-box" }}>
      {/* Back */}
      <button type="button" className="btn btn-ghost" onClick={() => navigate("/user-management")}
        style={{ fontSize: 13, marginBottom: "var(--space-lg)" }}>
        ← ניהול משתמשים
      </button>

      {/* ── Profile card ── */}
      <div style={{
        background: "var(--card-bg)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)", padding: "var(--space-xl)", marginBottom: "var(--space-lg)",
      }}>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-lg)", flexWrap: "wrap" }}>
          {/* Avatar */}
          <div style={{
            width: 56, height: 56, borderRadius: "50%", background: "var(--accent)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 700, flexShrink: 0,
          }}>
            {user.firstName?.[0]}{user.lastName?.[0]}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)", flexWrap: "wrap", marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{user.firstName} {user.lastName}</h1>
              <Badge label={displayRole}   color="var(--accent)" />
              <Badge label={displayStatus} color={statusColor} />
            </div>
            <div style={{ fontSize: 14, color: "var(--text-secondary)" }}>{user.email}</div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>
              הצטרף: {formatDate(user.createdAt, true)}
              {user.lastLoginAt && <> · כניסה אחרונה: {formatDate(user.lastLoginAt)}</>}
            </div>
          </div>

          {!isSelf && (
            <button type="button" className={`btn ${editing ? "btn-ghost" : "btn-primary"}`}
              onClick={() => { setEditing(!editing); setSaveError(""); }}
              style={{ alignSelf: "flex-start" }}>
              {editing ? "ביטול" : "ערוך"}
            </button>
          )}
        </div>

        {/* ── Unified editor ── */}
        {editing && !isSelf && (
          <div style={{ marginTop: "var(--space-lg)", paddingTop: "var(--space-lg)", borderTop: "1px solid var(--border-light)" }}>

            {/* Status row */}
            <div style={{ marginBottom: "var(--space-lg)" }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: "var(--text-secondary)", marginBottom: 8, textTransform: "uppercase" }}>
                סטטוס
              </p>
              {isPending ? (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Badge label="ממתין לאישור" color="var(--warning, #f59e0b)" />
                  <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    כדי לאשר או לדחות, חזור לניהול משתמשים
                  </span>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <PillBtn label="פעיל"    active={editStatus === "ACTIVE"}   onClick={() => setEditStatus("ACTIVE")}   color="var(--success)" />
                  <PillBtn label="לא פעיל" active={editStatus === "INACTIVE"} onClick={() => setEditStatus("INACTIVE")} color="var(--danger)"  />
                </div>
              )}
            </div>

            {/* Role row */}
            <div style={{ marginBottom: activePreset === "CUSTOM" ? "var(--space-md)" : 0 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: "var(--text-secondary)", marginBottom: 8, textTransform: "uppercase" }}>
                תפקיד והרשאות
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["BACK_OFFICE", "MANAGEMENT", "ADMIN", "CUSTOM"].map((pk) => (
                  <PillBtn
                    key={pk}
                    label={PRESET_LABELS[pk]}
                    active={activePreset === pk}
                    onClick={() => handlePresetClick(pk)}
                  />
                ))}
              </div>
            </div>

            {/* Custom: system role + full permission toggles */}
            {activePreset === "CUSTOM" && (
              <div style={{ marginTop: "var(--space-md)" }}>
                {/* System role (used by backend middleware) */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 10, marginBottom: "var(--space-md)",
                  padding: "10px 14px", borderRadius: "var(--radius)",
                  background: "var(--bg-subtle)", border: "1px solid var(--border-light)",
                }}>
                  <span style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 600 }}>System role</span>
                  <select
                    className="select"
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    style={{ width: "auto", minWidth: 110, marginRight: 4 }}
                  >
                    <option value="VIEWER">Viewer</option>
                    <option value="EDITOR">Editor</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                  <span style={{ fontSize: 11, color: "var(--text-secondary)", flex: 1 }}>
                    משפיע על הרשאות API (לא על ה-UI)
                  </span>
                </div>

                {/* Permission toggles */}
                {PERM_SECTIONS.map((section) => (
                  <PermSection key={section.key} section={section} perms={perms} onChange={handleToggle} />
                ))}
              </div>
            )}

            {/* Save */}
            <div style={{ marginTop: "var(--space-lg)", display: "flex", alignItems: "center", gap: 12 }}>
              <button type="button" className="btn btn-primary" onClick={handleSaveAll} disabled={saving || isPending}>
                {saving ? "שומר..." : "שמור"}
              </button>
              {saveError && <span style={{ fontSize: 13, color: "var(--danger)" }}>{saveError}</span>}
            </div>
          </div>
        )}

        {/* View mode: show role + preset summary when not editing */}
        {!editing && (
          <div style={{ marginTop: "var(--space-lg)", paddingTop: "var(--space-lg)", borderTop: "1px solid var(--border-light)", fontSize: 13, color: "var(--text-secondary)" }}>
            {user.rolePreset
              ? <>תפקיד: <strong style={{ color: "var(--text-primary, var(--text))" }}>{PRESET_LABELS[user.rolePreset] || user.rolePreset}</strong>
                  {user.rolePreset === "CUSTOM" && <> · System role: <strong>{ROLE_LABEL[user.role] || user.role}</strong></>}
                  {" · "}לחץ על "ערוך" לצפייה ועריכה
                </>
              : <>לחץ על "ערוך" לצפייה ועריכת הרשאות</>
            }
          </div>
        )}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: "var(--space-lg)", borderBottom: "1px solid var(--border)" }}>
        {[
          { key: "activity",  label: `פעילות (${activity.length})` },
          { key: "approvals", label: `היסטוריית אישורים (${approvals.length})` },
        ].map(({ key, label }) => (
          <button key={key} type="button" onClick={() => setTab(key)} style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "8px 16px", fontSize: 14, fontWeight: tab === key ? 700 : 400,
            color: tab === key ? "var(--accent)" : "var(--text-secondary)",
            borderBottom: `2px solid ${tab === key ? "var(--accent)" : "transparent"}`,
            marginBottom: -1,
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Activity Tab ── */}
      {tab === "activity" && (
        <div style={{ background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
          {activity.length === 0 ? (
            <div style={{ padding: "var(--space-xl)", textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>אין פעילות רשומה</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                  <th style={thStyle}>תאריך</th>
                  <th style={thStyle}>פעולה</th>
                  <th style={thStyle}>סוג</th>
                  <th style={thStyle}>ישות</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((log) => (
                  <tr key={log._id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                    <td style={tdStyle}>{formatDate(log.createdAt)}</td>
                    <td style={tdStyle}>
                      <span style={{
                        display: "inline-block", padding: "2px 8px", borderRadius: 99,
                        fontSize: 11, fontWeight: 600,
                        background: (ACTION_COLOR[log.action] || "var(--text-secondary)") + "22",
                        color: ACTION_COLOR[log.action] || "var(--text-secondary)",
                      }}>
                        {ACTION_LABEL[log.action] || log.action}
                      </span>
                    </td>
                    <td style={tdStyle}>{ENTITY_LABEL[log.entityType] || log.entityType}</td>
                    <td style={tdStyle}>{log.entitySummary || log.entityId || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Approvals Tab ── */}
      {tab === "approvals" && (
        <div style={{ background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
          {approvals.length === 0 ? (
            <div style={{ padding: "var(--space-xl)", textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>אין היסטוריית אישורים</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                  <th style={thStyle}>תאריך</th>
                  <th style={thStyle}>מנהל שאישר</th>
                  <th style={thStyle}>פעולה</th>
                  <th style={thStyle}>תפקיד שהוקצה</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((log) => (
                  <tr key={log._id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                    <td style={tdStyle}>{formatDate(log.createdAt)}</td>
                    <td style={tdStyle}>{log.adminName}</td>
                    <td style={tdStyle}>
                      <Badge
                        label={log.action === "APPROVED" ? "אושר" : "נדחה"}
                        color={log.action === "APPROVED" ? "var(--success)" : "var(--danger)"}
                      />
                    </td>
                    <td style={tdStyle}>{log.assignedRole ? (ROLE_LABEL[log.assignedRole] || log.assignedRole) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: "10px 14px", textAlign: "right", fontWeight: 600, fontSize: 12, color: "var(--text-secondary)", whiteSpace: "nowrap" };
const tdStyle = { padding: "10px 14px", verticalAlign: "middle" };
